import { useState, useEffect, useContext, useMemo, useRef } from 'react'
import { Button, Center } from '@mantine/core'
import { Modal } from 'react-bootstrap'
import { changeTextCapital, checkRole, getDateYYYYMMDD } from '../../../_metronic/helpers'
import { useThemeMode } from '../../../_metronic/partials'
import { API_BASE_URL } from '../../../utils/contants'
import InputErrorBox from '../../components/InputErrorBox'
import ListPagination from '../../components/ListPagination'
import Select2 from '../../components/select2'
import roleContext from '../../context/roleContext'
import { setupAxios, useAuth } from '../../modules/auth'
import { InputErrorType, RoleContextValue } from '../../types/OtherTypes'
import { getSystemConfiguration } from '../../../api/ConfigurationAPI'
import { getHolidays } from '../../../api/HolidayAPI'
import {
  formatCreateAt,
  formatDates,
  getFilterLeave,
  getLeave,
  handleCancelButtonClick,
  handleLeaveActions,
  handleSubmit,
  validateForm,
} from './LeaveFunctions'
import API from '../../../api/APIUrl'
import { getLeaveTypeData, handleOtherInputChange } from '../../CommonFunction'
import SubmitCancleButton from '../../components/SubmitCancelButton'
import CustomFileInput from '../../components/CustomInput/CustomFileInput'
import CustomTextInput from '../../components/CustomInput/CustomTextInput'
import MyLeaveModal from './MyLeaveModal'
import ReasonRemarkModal from './ReasonRemarkModal'

interface InputFieldState {
  sender: string
  recipients: {
    to: string
    cc: string[];
  }
  attachment: File | null
  leaveType: string
  isAttachmentRequired: boolean
  reasonId: string
  reason: string
  leaveDate: {
    startDate: string
    endDate: string
    type: string
  }[]
}

const MyLeave = ({ getLeaveBalances, setBalance, getPieChartData, setPieChartDate }) => {
  const [leave, setLeave] = useState<any[]>([])
  const [leavePagination, setLeavePagination] = useState({page: 1, pageSize: 10, total: 0, totalPages: 0})
  const leaveRequestVersion = useRef(0)
  let roleState: RoleContextValue = useContext(roleContext)
  const [leaveType, setLeaveType] = useState<any[]>([])
  const { currentUser } = useAuth()
  const { mode } = useThemeMode()

  type OptionType = {
    label: string
    value: string
    email: string
  }
  const [APIStatus, setAPIStatus] = useState({
    loading: false,
    error: null,
    submitLoading: false,
  })

  const [financialYearConfig, setFinancialYearConfig] = useState<{
    startDate: Date | null,
    endDate: Date | null
  }>({
    startDate: null,
    endDate: null,
  })
  const [weekendDays, setWeekendDays] = useState<string[]>([])
  // const [holidays, setHolidays] = useState<string[]>([])
  const [holidays, setHolidays] = useState<any[]>([])

  const [SubmitAPIStatus, setSubmitAPIStatus] = useState({
    loading: false,
  })
  const [reasonModalOpen, setReasonModalOpen] = useState<InputErrorType | null>(null)
  const [reason, setReason] = useState<string>('')
  const [error, setError] = useState<InputErrorType>({})
  const [dateError, setDateError] = useState('')
  const [reasonRemarkModalOpen, setReasonRemarkModalOpen] = useState<string | null>(null)
  const [selectedColumn, setSelectedColumn] = useState('')
  const [leaveReasons, setLeaveReasons] = useState<any[]>([])
  const [selectedReasonMeta, setSelectedReasonMeta] = useState<{ isEmergency: boolean } | null>(null)
  const [reasonMetaList, setReasonMetaList] = useState<any[]>([])

  // ----- Earned Leave Reasons (Fixed List) -----
  const earnedLeaveReasons = [
    { label: 'Medical Leave', value: 'earned_medical', isEmergency: false },
    { label: 'Personal Leave', value: 'earned_personal', isEmergency: false },
    { label: 'Emergency Leave', value: 'earned_emergency', isEmergency: true },
  ];

  const initialState = {
    sender: '',
    recipients: {
      to: '',
      cc: [],
    },
    attachment: null,
    isAttachmentRequired: false,
    leaveType: 'Casual Leave',
    reason: '',
    reasonId: '',
    description: '',
    leaveDate: [
      {
        startDate: '',
        endDate: '',
        type: 'FULL',
      },
    ],
  }
  const [inputError, setInputError] = useState<any>({})
  const [inputField, setInputField] = useState<InputFieldState>(initialState)
  const [myLeaveModalOpen, setMyLeaveModalOpen] = useState(false)
  const [editId, setEditId] = useState(null)
  const [dropdownOptions, setDropdownOptions] = useState<OptionType[]>([])
  const [currentUserFull, setCurrentUserFull] = useState<any>(null)
  const [showRow, setShowRow] = useState<number[]>([])

  const themeStyles = useMemo(() => ({
    input: {
      backgroundColor: 'transparent',
      color: mode === 'dark' ? 'white' : 'var(--bs-gray-700)',
      border: 'none',
    },
    item: {
      fontSize: '13px',
      color: mode === 'dark' ? 'white' : 'black',
      '&:hover': {
        backgroundColor: mode === 'dark' ? 'var(--bs-gray-500)' : 'var(--bs-gray-200)',
      },
    },
    dropdown: {
      backgroundColor: mode === 'dark' ? '#333333' : 'white',
      border: 'none',
    },
  }), [mode])

  const sortedUserNames = useMemo(() =>
    (dropdownOptions || []).slice().sort((a, b) => a.label.localeCompare(b.label)),
    [dropdownOptions]
  )

  // Fetch financial year configuration, weekend settings, and holidays
  const fetchFinancialYearConfig = async () => {
    try {
      const configResponse = await getSystemConfiguration();
      if (configResponse?.data && configResponse.data.length > 0) {
        const config = configResponse.data[0];
        setFinancialYearConfig({
          startDate: new Date(config.financialYearStartDate),
          endDate: new Date(config.financialYearEndDate),
        });

        // Set weekend days from configuration
        if (config.weekendDays && Array.isArray(config.weekendDays)) {
          setWeekendDays(config.weekendDays);
        } else {
          // Default to Saturday and Sunday if no configuration
          setWeekendDays(['Saturday', 'Sunday']);
        }
      }
    } catch (error) {
      // Fallback to calendar year financial year if API fails
      const today = new Date();
      const currentYear = today.getFullYear();

      // Calendar year financial year: January 1 to December 31
      setFinancialYearConfig({
        startDate: new Date(`${currentYear}-01-01`),
        endDate: new Date(`${currentYear}-12-31`),
      });

      // Default weekend days
      setWeekendDays(['Saturday', 'Sunday']);
    }
  };

  // Fetch holidays
  // const fetchHolidays = async () => {
  //   try {
  //     const holidaysResponse = await getHolidays();
  //     if (holidaysResponse?.data && Array.isArray(holidaysResponse.data)) {
  //       // Extract dates from holiday objects and format them as YYYY-MM-DD
  //       const holidayDates = holidaysResponse.data.map((holiday: any) => {
  //         const date = new Date(holiday.date);
  //         const year = date.getFullYear();
  //         const month = String(date.getMonth() + 1).padStart(2, '0');
  //         const day = String(date.getDate()).padStart(2, '0');
  //         return `${year}-${month}-${day}`;
  //       });
  //       setHolidays(holidayDates);
  //     }
  //   } catch (error) {
  //     setHolidays([]);
  //   }
  // };
  // Fetch holidays
  const fetchHolidays = async () => {
    try {
      const holidaysResponse = await getHolidays();
      if (
        holidaysResponse?.data &&
        Array.isArray(holidaysResponse.data)
      ) {
        // store complete holiday objects
        // backend already sends:
        // isFloating
        // isApplicable
        setHolidays(holidaysResponse.data || []);
      } else {
        setHolidays([]);
      }
    } catch (error) {
      console.error("Error fetching holidays:", error);
      setHolidays([]);
    }
  };


  // Check if a date is disabled (weekend or holiday)
  // const isDateDisabled = (dateStr: string) => {
  //   // Check if date is a holiday
  //   if (holidays.includes(dateStr)) {
  //     return true;
  //   }

  //   // Check if date is a weekend
  //   const date = new Date(dateStr);
  //   const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });

  //   return weekendDays.includes(dayName);
  // };
  const isDateDisabled = (dateStr: string) => {
    // weekend check
    const date = new Date(dateStr);
    const dayName = date.toLocaleDateString(
      'en-US',
      { weekday: 'long' }
    );
    if (weekendDays.includes(dayName)) {
      return true;
    }
    // matching holiday
    const holiday = holidays.find((h: any) => {
      const holidayDate = new Date(h.date);
      const formattedHolidayDate =
        holidayDate.toISOString().split('T')[0];
      return formattedHolidayDate === dateStr;
    });
    // no holiday
    if (!holiday) {
      return false;
    }
    // regular holiday
    if (!holiday.isFloating) {
      return true;
    }
    // floating holiday
    // block ONLY if selected by employee
    if (
      holiday.isFloating &&
      holiday.isApplicable === true
    ) {
      return true;
    }
    // floating holiday NOT selected
      return false;
    };
  
  // Min date for Start Date input
  // Requirement: Only Emergency leave (reason) can be backdated. All others must not allow back dates.
  const getMinStartDate = () => {
    // Get today's date in YYYY-MM-DD format without timezone issues
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const todayStr = `${year}-${month}-${day}`;

    // Use selected reason meta (emergency flag) instead of leave type label
    const isEmergencyReason = selectedReasonMeta?.isEmergency === true;

    // if (isEmergencyReason) {
    //   // Allow backdated selection for emergency. Provide a very early min date so
    //   // UI date picker allows choosing past dates. Validation will still apply
    //   // other business rules (e.g., joining date restrictions) where relevant.
    //   return '2000-01-01';
    // }
    if (isEmergencyReason) {
      const minDate = new Date();
      minDate.setMonth(minDate.getMonth() - 1);
      return minDate.toISOString().split('T')[0];
    }
    // For all non-emergency leaves, do not allow back dates
    return todayStr;
    }

  // Get max date based on financial year end
  // NOTE: This function is no longer used for restricting leave end dates
  // but kept for potential future use in other parts of the application
  const getMaxEndDate = () => {
    // Get current year for financial year calculation
    const today = new Date();
    const year = today.getFullYear();
    const financialYearEndStr = `${year}-12-31`;

    // Use financial year end date from config if available, otherwise use Dec 31
    if (financialYearConfig.endDate) {
      const configEndDate = new Date(financialYearConfig.endDate);
      const configYear = configEndDate.getFullYear();
      const configMonth = String(configEndDate.getMonth() + 1).padStart(2, '0');
      const configDay = String(configEndDate.getDate()).padStart(2, '0');
      return `${configYear}-${configMonth}-${configDay}`;
    }

    // Fallback to Dec 31 of current year
    return financialYearEndStr;
  }

  const fetchNames = async () => {
    const { token } = setupAxios();
    try {
      const usersResponse = await fetch(`${API_BASE_URL}/${API.USER}?preset=dropdown&status=Active&ignoreAssociations=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const usersJson = await usersResponse.json()

      const userData: OptionType[] = usersJson?.data
        ?.filter((item) => item?.id !== currentUser?.id)
        .map((obj) => ({
          label: obj.firstName + ' ' + obj.lastName,
          value: obj.id,
          email: obj.email,
        })) || [];

      setDropdownOptions(userData);

      const userDetailsResponse = await fetch(`${API_BASE_URL}/${API.USER}/${currentUser?.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const userDetailsJson = await userDetailsResponse.json()
      setCurrentUserFull(userDetailsJson.data);

    } catch (error) {
      // Ensure dropdownOptions is set to empty array on error
      setDropdownOptions([]);
    }
  }

  const fetchLeaveReasons = async () => {
    try {
      const { token, tenantId } = setupAxios();

      const casualLeave = leaveType.find(
        (item) => item.label && item.label.toLowerCase().includes('casual')
      );
      const leaveTypeId = casualLeave ? casualLeave.value : '';

      if (!leaveTypeId) {
        console.log('No casual leave type found');
        setLeaveReasons([]);
        setReasonMetaList([]);
        return;
      }

      console.log('Fetching leave reasons for leaveTypeId:', leaveTypeId);
      const response = await fetch(
        `${API_BASE_URL}/leave-reasons?leaveTypeId=${leaveTypeId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-tenant-Id': `${tenantId}`
          },
        }
      );

      if (!response.ok) {
        console.error('Failed to fetch leave reasons:', response.status);
        setLeaveReasons([]);
        setReasonMetaList([]);
        return;
      }

      const reasonsJson = await response.json()
      const reasons = reasonsJson.data || [];
      console.log('Leave reasons fetched:', reasons);

      if (!Array.isArray(reasons) || reasons.length === 0) {
        console.log('No leave reasons found in response');
        setLeaveReasons([]);
        setReasonMetaList([]);
        return;
      }

      // Sort reasons: non-emergency first, then emergency
      const sortedReasons = reasons.sort((a: any, b: any) => {
        if (a.isEmergency === b.isEmergency) return 0;
        return a.isEmergency ? 1 : -1;
      });

      const formatted: any[] = sortedReasons.map((item: any) => ({
        value: item.id,
        label: item.isEmergency ? `${item.label} (Emergency)` : item.label,
        isEmergency: item.isEmergency,
      }));

      console.log('Formatted leave reasons:', formatted);
      setLeaveReasons(formatted);
      setReasonMetaList(reasons);
    } catch (error) {
      console.error('Error fetching leave reasons:', error);
      setLeaveReasons([]);
      setReasonMetaList([]);
    }
  }

  useEffect(() => {
    fetchNames();
    getLeaveTypeData(setLeaveType);
  }, []);

  // Auto-set type to FIRST_HALF for Short Leave
  useEffect(() => {
    const selectedLeaveType = leaveType.find(lt => lt.value === inputField.leaveType);
    const isShortLeave = selectedLeaveType?.label?.toLowerCase().includes('short') || false;

    if (isShortLeave) {
      setInputField((prev) => ({
        ...prev,
        leaveDate: prev.leaveDate.map(leave => ({
          ...leave,
          type: 'FIRST_HALF'
        }))
      }));
    }
  }, [inputField.leaveType, leaveType]);

  useEffect(() => {
    if (!myLeaveModalOpen || !currentUserFull) return;

    const rmAssociate = currentUserFull.associates?.find(a =>
      a.associateType === "RM" || a.associateType === "REPORTING_MANAGER"
    );
    const hrAssociate = currentUserFull.associates?.find(a =>
      a.associateType === "HR" || a.associateType === "HR_MANAGER"
    );
    const rmId = rmAssociate?.userAssociate?.id
    const hrId = hrAssociate?.userAssociate?.id


    // Validate that employee has a reporting manager
    if (!rmId) {
      setInputError((prev: any) => ({
        ...prev,
        to: 'No reporting manager assigned. Please contact HR to assign a reporting manager.',
      }));
    } else {
      setInputError((prev: any) => ({
        ...prev,
        to: '',
      }));
    }

    // Only set recipients if not already set or if modal just opened
    setInputField(prev => {
      // If already set, don't overwrite unless modal just opened
      if (prev.recipients.to === rmId && !myLeaveModalOpen) {
        return prev;
      }
      return {
        ...prev,
        recipients: {
          ...prev.recipients,
          to: rmId || '',
          cc: [], // Don't auto-select CC as it's optional and can be multiple recipients
        },
      };
    });
  }, [myLeaveModalOpen, currentUserFull, dropdownOptions]);

  useEffect(() => {
    if (myLeaveModalOpen && inputField.leaveType?.toLowerCase().includes("casual")) {
      fetchLeaveReasons();
    }
  }, [myLeaveModalOpen, inputField.leaveType])

  useEffect(() => {
    if (myLeaveModalOpen && leaveType.length > 0) {
      const casualLeave = leaveType.find((item) =>
        item?.label?.toLowerCase().includes('casual')
      )

      if (casualLeave) {
        setInputField((prev) => ({
          ...prev,
          leaveType: casualLeave.value,
        }))
      }
    }
  }, [myLeaveModalOpen, leaveType])

  const handleTrashClick = () => {
    setInputField(initialState)
    setDateError('')
    setInputError({})
  }

  const checkArrayElement = (element: number) => {
    let arr: number[] = [...showRow]
    let isExists = false
    for (let i = 0; i <= arr.length - 1; i++) {
      if (arr[i] === element) {
        arr.splice(i, 1)
        isExists = true
        break
      }
    }
    if (!isExists) {
      arr.push(element)
    }
    setShowRow(arr)
  }

  const truncateString = (text, maxLength) => {
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + '...'
    }
    return text
  }

  const removeLeaveDate = (index) => {
    setInputField((prevInputField) => ({
      ...prevInputField,
      leaveDate: prevInputField.leaveDate.filter((_, i) => i !== index),
    }))
    setInputError('')
  }

  const addLeaveDate = () => {
    setInputField((prevInputField) => ({
      ...prevInputField,
      leaveDate: [
        ...prevInputField.leaveDate,
        {
          startDate: '',
          endDate: '',
          type: 'FULL',
        },
      ],
    }))
  }

  const handleStartDateChange = (e, index) => {
    const selectedStartDate = new Date(e.target.value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const joinDateStr = currentUser?.joiningDate ?? '';
    const joinDate = new Date(joinDateStr);

    // Check if selected reason is emergency
    const selectedReason = reasonMetaList.find((item: any) => item.id === inputField.reasonId);
    const isEmergency = selectedReason?.isEmergency || false;

    // Clear previous errors
    setInputError((prev) => ({
      ...prev,
      [`startDate${index}`]: '',
    }));

    // if (isEmergency) {
    //   // Emergency leave: must be at least 0.5 days (12 hours) in advance or can be immediate
    //   const diffInHours = (selectedStartDate.getTime() - today.getTime()) / (1000 * 60 * 60);

    //   // Allow past dates for emergency (can be applied immediately)
    //   if (diffInHours < -24) { // More than 1 day in the past
    //     setInputError((prev) => ({
    //       ...prev,
    //       [`startDate${index}`]: 'Emergency leave cannot be applied for dates more than 1 day in the past.',
    //     }));
    //     return;
    //   }

    //   if (diffInHours > 12) {
    //     setInputError((prev) => ({
    //       ...prev,
    //       [`startDate${index}`]: 'Emergency leave can only be applied up to 12 hours (0.5 day) in advance. For advance planning, use regular casual leave.',
    //     }));
    //     return;
    //   }
    if (isEmergency) {
      const currentDate = new Date();
      const currentDateStr = currentDate.toISOString().split('T')[0];
      // Previous 1 month same date
      const minDate = new Date();
      minDate.setMonth(minDate.getMonth() - 1);
      const minDateStr = minDate.toISOString().split('T')[0];
      const selectedDateStr = e.target.value;
      // Older than 1 month
      if (selectedDateStr < minDateStr) {
        setInputError((prev) => ({
          ...prev,
          [`startDate${index}`]:
            'Emergency Leave can only be applied within previous 1 month.',
        }));
        return;
      }
      // Future date not allowed
      if (selectedDateStr > currentDateStr) {
        setInputError((prev) => ({
          ...prev,
          [`startDate${index}`]:
            'Future dates are not allowed for Emergency Leave.',
        }));
        return;
      }
    } else {
      // Regular casual leave: allow dates from today and in the past (for retroactive applications)
      // Remove the 2-day advance restriction to allow flexible date selection
      // Business logic can be enforced by backend or admin approval process

      // Only prevent selecting dates too far in the future (beyond today)
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];

      if (selectedStartDate.toISOString().split('T')[0] > todayStr) {
        setInputError((prev) => ({
          ...prev,
          [`startDate${index}`]: 'Leave cannot be applied for future dates beyond today.',
        }));
        return;
      }
    }

    // Rule: No leave before joining date anniversary
    const joinDDMM = new Date(today.getFullYear(), joinDate.getMonth(), joinDate.getDate());
    const selectedDDMM = new Date(today.getFullYear(), selectedStartDate.getMonth(), selectedStartDate.getDate());
    if (selectedDDMM < joinDDMM) {
      setInputError((prev) => ({
        ...prev,
        [`startDate${index}`]: 'Leave cannot be applied before your joining date anniversary',
      }));
      return;
    }

    // Rule: Casual Leave cannot be availed for first month after joining
    const isCasualLeave = inputField.leaveType?.toLowerCase().includes('casual');
    if (isCasualLeave && joinDate) {
      const oneMonthAfterJoining = new Date(joinDate);
      oneMonthAfterJoining.setMonth(oneMonthAfterJoining.getMonth() + 1);

      if (selectedStartDate < oneMonthAfterJoining) {
        setInputError((prev) => ({
          ...prev,
          [`startDate${index}`]: `Casual Leave cannot be availed during the first month after joining. You can apply from ${oneMonthAfterJoining.toDateString()}.`,
        }));
        return;
      }
    }

    // Clear error if all is valid
    setInputError((prev) => ({
      ...prev,
      [`startDate${index}`]: '',
    }));

    // Update startDate in leaveDate array
    const updatedLeaveDate = [...inputField.leaveDate];
    updatedLeaveDate[index].startDate = e.target.value;

    setInputField((prevInputField) => ({
      ...prevInputField,
      leaveDate: updatedLeaveDate,
      isAttachmentRequired: false,
    }));
  }

  const handleEndDateChange = (e, index) => {
    const selectedEnd = new Date(e.target.value);
    const selectedStart = new Date(inputField.leaveDate[index].startDate);

    // Rule 2: End Date - Start Date ≤ 3 days (add 1 to include both start and end dates)
    const diffInTime = selectedEnd.getTime() - selectedStart.getTime();
    let diffInDays = diffInTime / (1000 * 3600 * 24);

    // For same date, this will be 0 and won't trigger the error (handled elsewhere)
    // For different dates, add 1 to get inclusive day count
    if (diffInDays > 0) {
      diffInDays = diffInDays + 1;
    }

    if (!isNaN(diffInDays) && diffInDays > 3) {
      setDateError('Leave duration cannot exceed 3 days.');
    } else {
      setDateError('');
    }

    setInputField((prevInputField) => ({
      ...prevInputField,
      leaveDate: [
        ...prevInputField.leaveDate.slice(0, index),
        {
          ...prevInputField.leaveDate[index],
          endDate: e.target.value,
        },
        ...prevInputField.leaveDate.slice(index + 1),
      ],
      isAttachmentRequired: false,
    }));
  }

  const handleTypeChange = (e, leaveIndex) => {
    const { value } = e.target

    setInputField((prevInputField) => ({
      ...prevInputField,
      leaveDate: prevInputField.leaveDate.map((leave, index) =>
        index === leaveIndex ? { ...leave, type: value } : leave
      ),
    }))
  }

  // const fetchData = () => {
  //   getLeaveBalances(currentUser, setBalance, setAPIStatus)
  //   getLeave(currentUser, setLeave, setAPIStatus, 'sender')
  //   getPieChartData(currentUser, setPieChartDate, setAPIStatus)
  // }
  const fetchLeavePage = (page = leavePagination.page) => {
    const requestVersion = ++leaveRequestVersion.current
    getLeave(currentUser, (data: any[]) => {
      const rows = Array.isArray(data) ? data : []

      const sortedRows = rows.sort(
        (a, b) => new Date(b?.createdAt).getTime() - new Date(a?.createdAt).getTime()
      )

      setLeave(sortedRows)
    }, setAPIStatus, 'sender', {
      page,
      pageSize: leavePagination.pageSize,
      isCurrent: () => leaveRequestVersion.current === requestVersion,
      onPageInfo: (pagination) => setLeavePagination((previous) => ({...previous, ...pagination})),
    })
  }

  const fetchData = () => {
    getLeaveBalances(currentUser, setBalance, setAPIStatus)
    fetchLeavePage()
    getPieChartData(currentUser, setPieChartDate, setAPIStatus)
  }

  useEffect(() => {
    let mounted = true
    if (mounted) {
      getLeaveTypeData(setLeaveType)
      fetchFinancialYearConfig()
      fetchHolidays()
    }
    return () => {
      mounted = false
    }
  }, [currentUser, getLeaveBalances])

  useEffect(() => {
    if (!currentUser?.id) return
    fetchLeavePage(leavePagination.page)
    return () => {
      leaveRequestVersion.current += 1
    }
  }, [currentUser?.id, leavePagination.page, leavePagination.pageSize])

  // Modal logic
  const handleOpenMyLeaveModal = () => setMyLeaveModalOpen(true)
  const handleCloseMyLeaveModal = () => {
    setInputError('')
    setMyLeaveModalOpen(false)
    setInputField(initialState)
    setLeaveReasons([])
    setReasonMetaList([])
    setSelectedReasonMeta(null)
  }

  // Check if user can apply for leave (Apply Leave Button show to all)
  //   // const canApplyLeave = () => {
  //   // Check if user is an intern who joined within a month
  //   if (currentUser?.joiningDate) {
  //     const joiningDate = new Date(currentUser.joiningDate);
  //     const today = new Date();
  //     const oneMonthAgo = new Date();
  //     oneMonthAgo.setMonth(today.getMonth() - 1);

  //     // Check if user role includes "Intern" and joined within the last month
  //     const isIntern = roleState.features.some(feature =>
  //       feature.toLowerCase().includes('intern')
  //     ) || currentUser?.role?.name?.toLowerCase().includes('intern') || false;

  //     const joinedWithinMonth = joiningDate > oneMonthAgo;

  //     if (isIntern && joinedWithinMonth) {
  //       return false;
  //     }
  //   }

  //   // All other users (including older interns) can apply for leave
  //   return true;
  // }
  
  //(Apply Leave Button show only Admin & Employee)
  const canApplyLeave = () => {
  const roleName = roleState.roleName?.toUpperCase()

  const isAdmin = roleName === 'ADMIN'
  const isEmployee = roleName === 'EMPLOYEE'
  const isSuperAdmin =
    roleName === 'SUPER ADMIN' || roleName === 'SUPER_ADMIN'

  //  Do not allow Super Admin
  if (isSuperAdmin) {
    return false
  }

  // ✅ Allow only Admin & Employee
  if (!(isAdmin || isEmployee)) {
    return false
  }

  // ===== Existing Intern Restriction Logic =====
  if (currentUser?.joiningDate) {
    const joiningDate = new Date(currentUser.joiningDate)
    const today = new Date()
    const oneMonthAgo = new Date()
    oneMonthAgo.setMonth(today.getMonth() - 1)

    const isIntern =
      roleState.features.some(feature =>
        feature.toLowerCase().includes('intern')
      ) ||
      currentUser?.role?.name?.toLowerCase().includes('intern') ||
      false

    const joinedWithinMonth = joiningDate > oneMonthAgo

    if (isIntern && joinedWithinMonth) {
      return false
    }
  }

  return true
}


  const handleOpenReasonRemarkModal = (html: string, title: string) => {
    setReasonRemarkModalOpen(html)
    setSelectedColumn(title)
  }
  const handleCloseReasonRemarkModal = () => setReasonRemarkModalOpen(null)

  // === FIX: Add onSubmit ===
  const onSubmit = () => {
    handleSubmit(
      inputField,
      initialState,
      // setLeave,
      () => {},
      setInputField,
      editId,
      setAPIStatus,
      setInputError,
      setSubmitAPIStatus,
      validateForm,
      setMyLeaveModalOpen,
      fetchData
    );
  };

  return (
    <div>
      <ReasonRemarkModal
        open={reasonRemarkModalOpen !== null}
        onClose={handleCloseReasonRemarkModal}
        html={reasonRemarkModalOpen}
        columnTitle={selectedColumn}
      />
      <MyLeaveModal
        open={myLeaveModalOpen}
        onClose={handleCloseMyLeaveModal}
        inputField={inputField}
        setInputField={setInputField}
        leaveType={leaveType}
        sortedUserNames={sortedUserNames}
        inputError={inputError}
        setInputError={setInputError}
        dateError={dateError}
        setDateError={setDateError}
        handleTypeChange={handleTypeChange}
        removeLeaveDate={removeLeaveDate}
        addLeaveDate={addLeaveDate}
        getMinStartDate={getMinStartDate}
        getMaxEndDate={getMaxEndDate}
        isDateDisabled={isDateDisabled}
        leaveReasons={leaveReasons}
        reasonMetaList={reasonMetaList}
        selectedReasonMeta={selectedReasonMeta}
        setSelectedReasonMeta={setSelectedReasonMeta}
        SubmitAPIStatus={SubmitAPIStatus}
        onSubmit={onSubmit}
        onTrash={handleTrashClick}
        themeStyles={themeStyles}
        currentUser={currentUser}
        currentUserFull={currentUserFull}
        dropdownOptions={dropdownOptions}

      />
      <div className='card mb-5 mb-xl-8'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>My Leave</span>
          </h3>
          <div
            className='d-flex mt-4 justify-content-end'
            data-kt-user-table-toolbar='base'
            style={{ height: '3rem' }}
          >
            <Button
              hidden={!canApplyLeave()}
              type='button'
              className='btn btn-sm btn-primary h-35px mt-1'
              onClick={handleOpenMyLeaveModal}
            >
              Apply Leave
            </Button>
          </div>
        </div>

        <div className='card-body py-3 '>
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
              <thead>
                <tr className='fw-bold text-muted '>
                  <th className=''></th>
                  <th className='' style={{ minWidth: '70px' }}>
                    {changeTextCapital('Subject')}
                  </th>
                  <th className='' style={{ minWidth: '110px' }}>
                    {changeTextCapital('Primary Reciepient')}
                  </th>
                  <th className='' style={{ minWidth: '110px' }}>
                    {changeTextCapital('Reason')}
                  </th>
                  <th className='min-w-150px'>{changeTextCapital('Remark')}</th>
                  <th className='' style={{ minWidth: '150px' }}>
                    {changeTextCapital('Status')}
                  </th>
                  <th className='' style={{ minWidth: '110px' }}>
                    {changeTextCapital('Create Date')}
                  </th>
                </tr>
              </thead>
              {APIStatus.loading ? (
                <tbody>
                  <tr>
                    <td colSpan={7}><Center>Loading leave requests...</Center></td>
                  </tr>
                </tbody>
              ) : leave?.length > 0 ? (
                <tbody>
                  {leave.map((obj: any, index: number) => (
                    <>
                      <tr>
                        <td>
                          <div>
                            <span
                              className={`badge badge-light-primary d-inline-block cursor-pointer ${showRow.includes(
                                index
                              )}`}
                              onClick={() => {
                                checkArrayElement(index)
                              }}
                            >
                              {showRow.includes(index) ? '↑' : '↓'}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className='d-flex align-items-center'>
                            <div className='d-flex justify-content-start flex-column'>
                              {`Request for ${obj?.leaveType?.name}`}
                            </div>
                          </div>
                        </td>
                        <td>
                          {/* {obj?.LeaveApprover.map((user) =>
                            user?.level === 1
                              ? `${user?.User?.firstName ?? '-'} ${user?.User?.lastName ?? '-'}`
                              : ''
                          )} */}
                            {(() => {
                              const recipients =
                                obj?.LeaveApprover
                                  ?.filter((user) => user?.level === 1)
                                  ?.map(
                                    (user) =>
                                      `${user?.User?.firstName ?? '-'} ${user?.User?.lastName ?? '-'}`
                                  ) || []

                              const isExpanded = showRow.includes(index)
                              return (
                                <>
                                  {isExpanded
                                    ? recipients.join(', ')
                                    : recipients.slice(0, 2).join(', ')}

                                  {recipients.length > 2 && (
                                    <span
                                      className='text-primary ms-2 cursor-pointer fw-bold'
                                      onClick={() => checkArrayElement(index)}
                                    >
                                      {isExpanded
                                        ? 'Read Less'
                                        : `+${recipients.length - 2} More`}
                                    </span>
                                  )}
                                </>
                              )
                            })()}
                          </td>
                        <td
                          className=' cursor-pointer mt-5'
                          onClick={() => {
                            const reasonText = [
                              obj?.reason?.label,
                              obj?.description
                            ].filter(Boolean).join(' - ') || '-';
                            handleOpenReasonRemarkModal(
                              reasonText?.replaceAll('<p>', '')?.replaceAll('</p>', ''),
                              'Reason'
                            )
                          }}
                          dangerouslySetInnerHTML={{
                            __html: truncateString(
                              [
                                obj?.reason?.label,
                                obj?.description
                              ].filter(Boolean).join(' - ')?.replaceAll('<p>', '')?.replaceAll('</p>', '') || '-',
                              20
                            ),
                          }}
                        />
                        <td
                          className=' cursor-pointer mt-5'
                          onClick={() => {
                            handleOpenReasonRemarkModal(
                              obj?.LeaveApprover?.filter((item) => item?.status !== 'PENDING')
                                ?.length > 0
                                ? obj?.LeaveApprover?.filter(
                                  (item) => item?.status !== 'PENDING'
                                )[0]?.remarks
                                : '-',
                              'Remark'
                            )
                          }}
                          dangerouslySetInnerHTML={{
                            __html: truncateString(
                              obj?.LeaveApprover?.filter((item) => item?.status !== 'PENDING')
                                ?.length > 0
                                ? obj?.LeaveApprover?.filter(
                                  (item) => item?.status !== 'PENDING'
                                )[0]?.remarks
                                : '-',
                              20
                            ),
                          }}
                        />
                        <td>
                          <span
                            className={`badge border ${['APPROVED', 'AUTO_APPROVED'].includes(obj?.status)
                              ? 'badge-light-success'
                              : obj?.status === 'CANCELLED'
                                ? 'bg-secondary'
                                : obj?.status?.includes('REJECTED')
                                  ? 'badge-light-danger'
                                  : obj?.status === 'partially approved'
                                    ? 'badge badge-light-success'
                                    : obj?.status === 'partially declined'
                                      ? 'badge badge-light-danger'
                                      : 'badge badge-light-warning'
                              }`}
                          >
                            {obj?.status === 'AUTO_APPROVED' ? 'AUTO APPROVED' : obj?.status}
                          </span>
                        </td>

                        <td>{formatCreateAt(obj?.createdAt ?? '-')}</td>
                      </tr>

                      <tr key={`secondIndex${index}`} hidden={!showRow.includes(index)}>
                        <td colSpan={7}>
                          <>
                            <div className='table-responsive'>
                              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                                <thead>
                                  <tr className='fw-bold text-muted'>
                                    <th style={{ width: '35px' }}></th>{' '}
                                    <th style={{ width: '175px' }}>{changeTextCapital('Type')}</th>
                                    <th style={{ width: '160px' }}>
                                      {changeTextCapital('Start Date')}
                                    </th>
                                    <th style={{ width: '115px' }}>
                                      {changeTextCapital('End Date')}
                                    </th>
                                    <th style={{ width: '115px' }}>
                                      {changeTextCapital('Attachment')}
                                    </th>
                                    <th style={{ width: '150px' }}>{changeTextCapital('Status')}</th>
                                    <th style={{ width: '140px' }}>
                                      {changeTextCapital('Action By')}
                                    </th>
                                  </tr>
                                </thead>

                                <tbody>
                                  <tr>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                  </tr>

                                  <>
                                    <tr key={`${obj?.id ?? '-'}-${index}`}>
                                      <td>
                                        <div>
                                          <span></span>
                                        </div>
                                      </td>
                                      <td>
                                        <div className='d-flex align-items-center'>
                                          <div className='d-flex justify-content-start flex-column'>
                                            {obj?.leaveDuration}
                                          </div>
                                        </div>
                                      </td>
                                      <td>{formatDates(obj.startDate ?? '-')}</td>
                                      <td>
                                        <div className='d-flex flex-column '>
                                          {formatDates(obj.endDate ?? '-')}
                                        </div>
                                      </td>
                                      <td>
                                        {obj?.attachment ? (
                                          <Button
                                            variant='outline'
                                            color='gray'
                                            onClick={() => {
                                              const root = (process.env.REACT_APP_API_KEY || '').replace(/\/api\/v1\/?$/, '');
                                              const attachmentUrl = `${root}/storage/leave/${obj?.attachment}`;
                                              window.open(attachmentUrl, '_blank');
                                            }}
                                          >
                                            <i className='fa-solid fa-eye'></i>
                                          </Button>
                                        ) : (
                                          '-'
                                        )}
                                      </td>
                                      <td className=' align-middle'>
                                        {['PENDING', 'REVIEW']?.includes(obj?.status) ? (
                                          <span className='badge badge-light-warning d-inline-block'>
                                            {obj?.status}
                                          </span>
                                        ) : (
                                          <span
                                            className={`badge d-inline-block ${['APPROVED', 'AUTO_APPROVED'].includes(obj?.status)
                                              ? 'badge-light-success'
                                              : obj?.status === 'CANCELLED'
                                                ? 'bg-secondary'
                                                : 'badge-light-danger'
                                              }`}
                                          >
                                            {obj?.status === 'AUTO_APPROVED' ? 'AUTO APPROVED' : obj?.status}
                                          </span>
                                        )}
                                      </td>
                                      {['PENDING', 'REVIEW']?.includes(obj?.status) ? (
                                        <td className=' align-middle'>
                                          {['REVIEW']?.includes(obj?.status) ? (
                                            '-'
                                          ) : (
                                            <button
                                              type='button'
                                              className='btn btn-secondary btn-sm'
                                              onClick={() => {
                                                setReasonModalOpen({
                                                  leaveId: obj?.id,
                                                  status: 'CANCELLED',
                                                })
                                              }}
                                            >
                                              Cancel
                                            </button>
                                          )}
                                        </td>
                                      ) : (
                                        <td className='align-middle'>
                                          {obj?.status === 'CANCELLED' ? (
                                            <>
                                              <div className='mt-5'>
                                                {currentUser?.firstName ?? '-'}{' '}
                                                {currentUser?.lastName ?? '-'}
                                              </div>
                                              <div className='text-muted'>
                                                {obj?.updatedAt
                                                  ? formatCreateAt(obj?.updatedAt ?? '-')
                                                  : '-'}
                                              </div>
                                            </>
                                          ) : (
                                            <>
                                              {obj?.LeaveApprover?.filter(
                                                (item) => item?.status !== 'PENDING'
                                              )?.map(
                                                (item, index) =>
                                                  index == 0 && (
                                                    <>
                                                      <div className='mt-5'>
                                                        {item?.User?.firstName ?? '-'}{' '}
                                                        {item?.User?.lastName ?? '-'}
                                                      </div>
                                                      <div className='text-muted'>
                                                        {item?.updatedAt
                                                          ? formatCreateAt(item?.updatedAt ?? '-')
                                                          : '-'}
                                                      </div>
                                                    </>
                                                  )
                                              )}
                                            </>
                                          )}
                                        </td>
                                      )}
                                    </tr>
                                  </>
                                </tbody>
                              </table>
                            </div>
                          </>
                        </td>
                      </tr>
                    </>
                  ))}
                </tbody>
              ) : (
                <tbody>
                  <tr>
                    <td colSpan={7}>
                      <Center>No Record Available.</Center>
                    </td>
                  </tr>
                </tbody>
              )}
            </table>
          </div>
        </div>
        <div className='px-4 pb-4'>
          <ListPagination
            page={leavePagination.page}
            pageSize={leavePagination.pageSize}
            totalRecords={leavePagination.total}
            disabled={APIStatus.loading}
            onPageChange={(page) => setLeavePagination((previous) => ({...previous, page}))}
            onPageSizeChange={(pageSize) => setLeavePagination((previous) => ({...previous, page: 1, pageSize}))}
          />
        </div>
      </div>

      {/* Cancel Leave Modal */}
      <Modal show={reasonModalOpen !== null} animation={false} backdrop='static' centered>
        <div className='modal-header'>
          <h2 className='fw-bolder'>Cancel Leave Request</h2>
          <div
            className='btn btn-icon btn-sm btn-active-icon-danger'
            data-kt-users-modal-action='close'
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setError({})
              setReason('')
              setReasonModalOpen(null)
            }}
          >
            <i className='fa-solid fa-xmark fs-1' />
          </div>
        </div>
        <div className='m-3'>
          <div className='row ms-3'>
            <div className='fv-row me-6 col-md-10 col-10 mb-3'>
              <label className='form-label'>Reason for Cancellation</label>
              <CustomTextInput
                value={reason}
                onChange={(event) => setReason(event?.target?.value)}
                placeholder='Please provide a reason for cancelling this leave request'
                error={error.reason}
              />
            </div>
          </div>
          <SubmitCancleButton
            cancle={{
              onClick: () => {
                setError({})
                setReason('')
                setReasonModalOpen(null)
              },
            }}
            submit={{
              editid: null,
              label: 'Cancel Leave',
              loading: SubmitAPIStatus.loading,
              onClick: () =>
                handleLeaveActions(
                  {
                    leaveId: reasonModalOpen?.leaveId ?? '',
                    status: reasonModalOpen?.status ?? '',
                    currentUser: currentUser,
                    reason: reason,
                  },
                  setReason,
                  setReasonModalOpen,
                  setError,
                  // setLeave,
                  () => {},
                  setAPIStatus,
                  setSubmitAPIStatus,
                  'sender',
                  fetchData
                ),
            }}
          />
        </div>
      </Modal>
    </div>
  )
}
export default MyLeave
